import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "field",
    "error",
    "useCaseGroup",
    "useCaseOption",
    "useCaseCustomToggle",
    "useCaseCustom",
    "useCaseCustomText",
    "useCaseValue"
  ]

  connect() {
    this.syncUseCase()
  }

  submit(event) {
    this.syncUseCase()
    const invalidFields = this.fieldTargets.filter((field) => !this.validateField(field))

    if (invalidFields.length === 0) return

    event.preventDefault()
    const firstField = invalidFields[0]
    const focusTarget = this.focusTargetFor(firstField)
    focusTarget.focus({ preventScroll: true })
    focusTarget.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  clearField(event) {
    const field = event.target

    if (this.isUseCaseControl(field)) {
      this.syncUseCase()
      this.clearUseCaseIfValid()
    }

    if (!this.fieldTargets.includes(field)) return

    if (this.fieldIsValid(field)) this.clearFieldError(field)
  }

  validateField(field) {
    const errorMessage = this.errorMessageFor(field)

    if (errorMessage) {
      this.showFieldError(field, errorMessage)
      return false
    }

    this.clearFieldError(field)
    return true
  }

  fieldIsValid(field) {
    return !this.errorMessageFor(field)
  }

  errorMessageFor(field) {
    if (field.dataset.validationType === "use_case") {
      return this.useCaseErrorMessage(field)
    }

    const value = field.value.trim()

    if (field.required && value.length === 0) {
      return field.dataset.requiredMessage
    }

    if (value.length === 0) return null

    if (field.dataset.validationType === "email" && field.validity.typeMismatch) {
      return field.dataset.emailMessage
    }

    if (field.dataset.validationType === "url" && !this.validHttpUrl(value)) {
      return field.dataset.urlMessage
    }

    return null
  }

  useCaseErrorMessage(field) {
    const selectedOptions = this.selectedUseCaseOptions()
    const customSelected = this.hasUseCaseCustomToggleTarget && this.useCaseCustomToggleTarget.checked
    const customText = this.hasUseCaseCustomTextTarget ? this.useCaseCustomTextTarget.value.trim() : ""

    if (selectedOptions.length === 0 && !customSelected) {
      return field.dataset.requiredMessage
    }

    if (customSelected && selectedOptions.length === 0 && customText.length === 0) {
      return field.dataset.customMessage
    }

    return null
  }

  showFieldError(field, message) {
    const error = this.errorTargetFor(field)
    if (!error) return

    field.classList.add("is-error")
    field.setAttribute("aria-invalid", "true")
    this.addDescribedBy(field, error.id)
    this.syncUseCaseCustomErrorState(field)

    error.textContent = message
    error.hidden = false
  }

  clearFieldError(field) {
    const error = this.errorTargetFor(field)
    if (!error) return

    field.classList.remove("is-error")
    field.removeAttribute("aria-invalid")
    this.removeDescribedBy(field, error.id)
    this.syncUseCaseCustomErrorState(field)

    error.textContent = ""
    error.hidden = true
  }

  syncUseCase() {
    if (!this.hasUseCaseGroupTarget || !this.hasUseCaseValueTarget) return

    if (this.hasUseCaseCustomTarget && this.hasUseCaseCustomToggleTarget) {
      this.useCaseCustomTarget.hidden = !this.useCaseCustomToggleTarget.checked
      if (!this.useCaseCustomToggleTarget.checked && this.hasUseCaseCustomTextTarget) {
        this.useCaseCustomTextTarget.value = ""
      }
    }

    const values = this.selectedUseCaseOptions()

    if (this.hasUseCaseCustomToggleTarget && this.useCaseCustomToggleTarget.checked && this.hasUseCaseCustomTextTarget) {
      const customText = this.useCaseCustomTextTarget.value.trim()
      if (customText.length > 0) values.push(customText)
    }

    this.useCaseValueTarget.value = values.join("\n")
  }

  clearUseCaseIfValid() {
    if (!this.hasUseCaseGroupTarget) return
    if (this.fieldIsValid(this.useCaseGroupTarget)) this.clearFieldError(this.useCaseGroupTarget)
  }

  selectedUseCaseOptions() {
    return this.useCaseOptionTargets
      .filter((option) => option.checked)
      .map((option) => option.value)
  }

  isUseCaseControl(field) {
    return (
      this.useCaseOptionTargets.includes(field) ||
      (this.hasUseCaseCustomToggleTarget && field === this.useCaseCustomToggleTarget) ||
      (this.hasUseCaseCustomTextTarget && field === this.useCaseCustomTextTarget)
    )
  }

  errorTargetFor(field) {
    return this.errorTargets.find((error) => error.dataset.fieldName === field.dataset.fieldName)
  }

  focusTargetFor(field) {
    if (this.hasUseCaseGroupTarget && field === this.useCaseGroupTarget) {
      if (
        this.hasUseCaseCustomToggleTarget &&
        this.useCaseCustomToggleTarget.checked &&
        this.hasUseCaseCustomTextTarget &&
        this.selectedUseCaseOptions().length === 0
      ) {
        return this.useCaseCustomTextTarget
      }

      return this.useCaseOptionTargets[0] || field
    }

    return field
  }

  addDescribedBy(field, id) {
    if (!id) return

    const ids = new Set((field.getAttribute("aria-describedby") || "").split(/\s+/).filter(Boolean))
    ids.add(id)
    field.setAttribute("aria-describedby", Array.from(ids).join(" "))
  }

  removeDescribedBy(field, id) {
    if (!id) return

    const ids = (field.getAttribute("aria-describedby") || "")
      .split(/\s+/)
      .filter((existingId) => existingId && existingId !== id)

    if (ids.length > 0) {
      field.setAttribute("aria-describedby", ids.join(" "))
    } else {
      field.removeAttribute("aria-describedby")
    }
  }

  validHttpUrl(value) {
    try {
      const url = new URL(value)
      return url.protocol === "http:" || url.protocol === "https:"
    } catch {
      return false
    }
  }

  syncUseCaseCustomErrorState(field) {
    if (!this.hasUseCaseGroupTarget || !this.hasUseCaseCustomTextTarget) return
    if (field !== this.useCaseGroupTarget) return

    const method = field.classList.contains("is-error") ? "add" : "remove"
    this.useCaseCustomTextTarget.classList[method]("is-error")

    if (field.getAttribute("aria-invalid") === "true") {
      this.useCaseCustomTextTarget.setAttribute("aria-invalid", "true")
    } else {
      this.useCaseCustomTextTarget.removeAttribute("aria-invalid")
    }
  }
}
